"use client";

import { useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

const SIZES = {
  20: { box: "h-5 w-5", padding: "p-0.5", text: "text-[9px]" },
  24: { box: "h-6 w-6", padding: "p-1", text: "text-[10px]" },
  32: { box: "h-8 w-8", padding: "p-1", text: "text-[10px]" },
  36: { box: "h-9 w-9", padding: "p-1", text: "text-xs" },
  56: { box: "h-14 w-14", padding: "p-2", text: "text-lg" },
} as const;

export type StockLogoSize = keyof typeof SIZES;

/** Zweiter, unabhaengiger Logo-Anbieter (Runde 8 Punkt 11: "Firmenlogos laden
 * weiterhin oft nicht", Erik: "darf nie passieren") - live verifiziert (2026-07-21,
 * AAPL/MSFT/SAP.DE/NESN.SW/TSLA, echte SVGs 675-18813 Bytes, ungueltiger Ticker
 * liefert sauber 404 statt eines leeren 200ers). Bewusst KEIN domain-basierter
 * Dienst (Google-Favicons/Clearbit) - haette `profile.website` als neuen Prop durch
 * alle 9 `StockLogo`-Aufrufstellen gebraucht (die meisten arbeiten mit schlanken
 * `ScoreSummary`-artigen Objekten ohne Website-Feld), Parqet arbeitet dagegen direkt
 * mit demselben Ticker-Symbol, das `StockLogo` ohnehin schon bekommt - kein einziger
 * Aufrufer muss angepasst werden. */
function fallbackLogoUrlForSymbol(symbol: string): string {
  return `https://assets.parqet.com/logos/symbol/${encodeURIComponent(symbol)}`;
}

/**
 * Aktienlogo mit Initialen-Fallback (erste zwei Buchstaben des Symbols), wenn kein
 * `logoUrl` vorliegt ODER das Bild tatsaechlich nicht laedt - vorher 7-fach
 * unabhaengig in Screener/Watchlist/Depot/Konkurrenzanalyse/Startseite/Heute-Seite
 * kopiert, hier konsolidiert. `bordered` default true (6 von 7 bisherigen Stellen
 * hatten einen Rahmen), `stock-pill-list.tsx` ist die einzige Ausnahme ohne Rahmen.
 *
 * BUGFIX (Erik 2026-07-17, Feedback-Runde 3 Punkt 8: "Hermès/Visa haben kein Logo"):
 * `logoUrlForSymbol()` liefert IMMER eine URL (nie undefined), der
 * "kein logoUrl"-Fallback unten war dadurch faktisch nie erreichbar - schlug das
 * eigentliche Bild fehl (FMPs kostenloser Bild-Host `images.financialmodelingprep.com`
 * live in dieser Session unerreichbar, live per curl/fetch bestaetigt, unabhaengig
 * vom Symbol), landete man bisher bei einem stillen kaputten `<img>` statt dem
 * eigentlich schon vorhandenen Initialen-Fallback. Braucht "use client" + State, um
 * `onError` tatsaechlich auf die Initialen umzuschalten (Next/Image selbst hat keinen
 * eingebauten Fallback-Mechanismus).
 *
 * BUGFIX 2 (Runde 8 Punkt 11): einstufiger Fallback (Bild -> Initialen) reichte nicht,
 * sobald der EINE Bild-Host selbst ausfaellt - jetzt zweistufig (FMP -> Parqet ->
 * Initialen), `failed`-State zaehlt statt eines Bools daher die tatsaechlich
 * fehlgeschlagenen Versuche.
 */
export function StockLogo({
  symbol,
  logoUrl,
  size = 24,
  bordered = true,
  className,
}: {
  symbol: string;
  logoUrl?: string;
  size?: StockLogoSize;
  bordered?: boolean;
  className?: string;
}) {
  const { box, padding, text } = SIZES[size];
  const [failedCount, setFailedCount] = useState(0);

  const sources = [logoUrl, fallbackLogoUrlForSymbol(symbol)].filter(
    (src): src is string => Boolean(src),
  );
  const src = sources[failedCount];

  if (src) {
    return (
      <Image
        key={src}
        src={src}
        alt=""
        width={size}
        height={size}
        unoptimized
        onError={() => setFailedCount((n) => n + 1)}
        className={cn(
          box,
          "shrink-0 rounded-full bg-white object-contain",
          padding,
          bordered && "border border-hairline",
          className,
        )}
      />
    );
  }

  return (
    <span
      className={cn(
        box,
        "flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground",
        text,
        className,
      )}
    >
      {symbol.slice(0, 2)}
    </span>
  );
}
